import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { verifyContactAPI } from 'api/verifyContactAPI';
import {
  INVALID,
  MAIL_UPDATE_FAILED_OBJ,
  MANHEIM_MAIL,
  MANHEIM_MAIL_FLAG,
  MANHEIM_MOBILE_TELEPHONE,
  MANHEIM_MOBILE_TELEPHONE_FLAG,
  NORMAL,
  PHONE_UPDATE_FAILED_OBJ,
  VALID,
} from 'constants/verifyContactConstants';
import { QueryClient } from 'react-query';
import { AppThunk } from 'redux/store';
import { getRawPhoneNumber } from 'utils/formatters';

type MailAndPhone = {
  [MANHEIM_MAIL]?: string;
  [MANHEIM_MOBILE_TELEPHONE]?: string;
};

type ConfirmFields = {
  [MANHEIM_MAIL]?: boolean;
  [MANHEIM_MOBILE_TELEPHONE]?: boolean;
};
type FieldsAfterConfirm = {
  [MANHEIM_MAIL]?: string;
  [MANHEIM_MOBILE_TELEPHONE]?: string;
};

type TextHelperType = typeof VALID | typeof INVALID | typeof NORMAL;
type TextHelperVariant = 'success' | 'error' | 'normal';

type TextHelper = {
  type: TextHelperType;
  variant: TextHelperVariant;
  value: string;
};

type FormValue = {
  [MANHEIM_MAIL]?: string;
  [MANHEIM_MOBILE_TELEPHONE]?: string;
};

export const calcFlag = (type: string) => {
  if (type === MANHEIM_MOBILE_TELEPHONE) {
    return MANHEIM_MOBILE_TELEPHONE_FLAG;
  }
  return MANHEIM_MAIL_FLAG;
};

const textHelperError: any = {
  [MANHEIM_MOBILE_TELEPHONE]: PHONE_UPDATE_FAILED_OBJ,
  [MANHEIM_MAIL]: MAIL_UPDATE_FAILED_OBJ,
};

interface VerifyContactState {
  initFormValue: FormValue;
  oldOwner: MailAndPhone;
  needConfirmFields: ConfirmFields;
  waitingFieldFireAFterConfirm: FieldsAfterConfirm;
  isOpenModal: boolean;
  textHelper: {
    [MANHEIM_MOBILE_TELEPHONE]: TextHelper;
    [MANHEIM_MAIL]: TextHelper;
  };
}

const initialState: VerifyContactState = {
  initFormValue: {
    manheimMail: '',
    manheimMobileTelephone: '',
  },
  oldOwner: {
    manheimMail: undefined,
    manheimMobileTelephone: undefined,
  },
  needConfirmFields: {},
  waitingFieldFireAFterConfirm: {},
  isOpenModal: false,
  textHelper: {
    manheimMobileTelephone: {
      type: NORMAL,
      variant: 'normal',
      value: '',
    },
    manheimMail: {
      type: NORMAL,
      variant: 'normal',
      value: '',
    },
  },
};

const slice = createSlice({
  name: 'verifyContact',
  initialState,
  reducers: {
    setInitFormValue(
      state: VerifyContactState,
      { payload }: PayloadAction<FormValue>
    ) {
      state.initFormValue = {
        ...state.initFormValue,
        ...payload,
      };
    },

    setOpenModal(
      state: VerifyContactState,
      { payload }: PayloadAction<boolean>
    ) {
      state.isOpenModal = payload;
    },
    setTextHelper(state: VerifyContactState, { payload }: PayloadAction<any>) {
      state.textHelper = {
        ...state.textHelper,
        ...payload,
      };
    },

    setNeedConfirmFields(
      state: VerifyContactState,
      { payload }: PayloadAction<ConfirmFields>
    ) {
      state.needConfirmFields = {
        ...state.needConfirmFields,
        ...payload,
      };
    },
    setFieldsFireAfterConfirm(
      state: VerifyContactState,
      { payload }: PayloadAction<FieldsAfterConfirm>
    ) {
      state.waitingFieldFireAFterConfirm = {
        ...state.waitingFieldFireAFterConfirm,
        ...payload,
      };
    },

    setOldOwner(
      state: VerifyContactState,
      { payload }: PayloadAction<MailAndPhone>
    ) {
      state.oldOwner = {
        ...state.oldOwner,
        ...payload,
      };
    },

    resetVerifyConfirmModal(state: VerifyContactState) {
      state.oldOwner = {};
      state.isOpenModal = false;
      state.needConfirmFields = {};
      state.waitingFieldFireAFterConfirm = {};
    },
  },
});

export const {
  setInitFormValue,
  setOpenModal,
  setNeedConfirmFields,
  setOldOwner,
  setTextHelper,
  setFieldsFireAfterConfirm,
  resetVerifyConfirmModal,
} = slice.actions;

const formatField = (type: string, value: string) => {
  if (type === MANHEIM_MOBILE_TELEPHONE) return getRawPhoneNumber(value);
  return value;
};

export const updateUser = (
  username: string,
  type: string,
  inputValue: string,
  queryClient: QueryClient
): AppThunk => async (dispatch) => {
  try {
    const value = formatField(type, inputValue);
    const flag = calcFlag(type);
    await verifyContactAPI.updateUser(username, {
      [type]: value,
      [flag]: false,
    });

    queryClient.setQueryData('user', (old: any) => {
      return {
        ...old,
        [flag]: 'false',
        [type]: value,
      };
    });

    dispatch(
      setInitFormValue({
        [type]: value,
      })
    );
  } catch (err) {
    dispatch(
      setTextHelper({
        [type]: textHelperError[type],
      })
    );
  }
};

export const updateBothField = (
  username: any,
  manheimMail: any,
  manheimMobileTelephoneInput: any,
  queryClient: QueryClient
): AppThunk => async (dispatch) => {
  try {
    const manheimMobileTelephone = formatField(
      MANHEIM_MOBILE_TELEPHONE,
      manheimMobileTelephoneInput
    );
    const newData = {
      [MANHEIM_MAIL]: manheimMail,
      [MANHEIM_MOBILE_TELEPHONE]: manheimMobileTelephone,
      [MANHEIM_MOBILE_TELEPHONE_FLAG]: false,
      [MANHEIM_MAIL_FLAG]: false,
    };
    await verifyContactAPI.updateUser(username, newData);

    queryClient.setQueryData('user', (old: any) => {
      return {
        ...old,
        ...newData,
      };
    });

    dispatch(
      setInitFormValue({
        [MANHEIM_MAIL]: manheimMail,
        [MANHEIM_MOBILE_TELEPHONE]: manheimMobileTelephone,
      })
    );
    // eslint-disable-next-line no-empty
  } catch (e) {}
};

export default slice.reducer;
